<!--
  Every figure here is a level - principal outstanding, interest accrued to date, a rate -
  never a flow, so none of them is signed (AGENTS.md: "A level ... is never signed").
  `annualInterestRate` is rendered through `formatRate`, never applied to money: the
  server's currency service is the only place a rate and an amount combine.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { formatDate, formatRate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { drawnState } from '../internal/drawn.ts'
import type { Loan } from '../internal/types.ts'

const props = defineProps<{ loan: Loan; hasHistory: boolean | undefined }>()
const { locale, t } = useI18n()

const drawn = computed(() => drawnState(props.loan, props.hasHistory))
const originalPrincipal = computed(() => props.loan.originalPrincipal)
const outstandingPrincipal = computed(() => props.loan.outstandingPrincipal)
const accruedInterest = computed(() => props.loan.accruedInterest)
const rate = computed(() =>
  formatRate(props.loan.annualInterestRate, { locale: locale.value as 'en' | 'hu' }),
)
const rateTypeLabel = computed(() => {
  if (props.loan.rateType === undefined) return t('loans.notSet')
  return props.loan.rateType === 'FIXED' ? t('loans.rateFixed') : t('loans.rateVariable')
})
</script>

<template>
  <dl class="grid grid-cols-2 gap-x-[var(--space-block)] gap-y-[var(--space-cell)]">
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.originalPrincipal') }}
      </dt>
      <dd>
        <MoneyAmount :value="originalPrincipal" />
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.outstandingPrincipal') }}
      </dt>
      <dd v-if="drawn === 'not-drawn'" class="text-[color:var(--color-text-muted)]">
        {{ t('loans.notYetDrawn') }}
      </dd>
      <dd v-else-if="drawn === 'unknown'" aria-hidden="true">&nbsp;</dd>
      <dd v-else>
        <MoneyAmount :value="outstandingPrincipal" />
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.accruedInterest') }}
      </dt>
      <dd>
        <MoneyAmount :value="accruedInterest" />
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.annualInterestRate') }}
      </dt>
      <dd class="text-[length:var(--text-step-3)] font-semibold tabular-nums text-[color:var(--color-text)]">
        {{ rate }}%
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.drawdownDate') }}
      </dt>
      <dd class="text-[color:var(--color-text)]">
        {{ formatDate(loan.drawdownDate, locale as 'en' | 'hu') }}
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.nextAccrualDate') }}
      </dt>
      <dd class="text-[color:var(--color-text)]">
        {{ formatDate(loan.nextAccrualDate, locale as 'en' | 'hu') }}
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.monthlyPayment') }}
      </dt>
      <dd v-if="loan.monthlyPayment === undefined" class="text-[color:var(--color-text-muted)]">
        {{ t('loans.notSet') }}
      </dd>
      <dd v-else>
        <MoneyAmount :value="loan.monthlyPayment" />
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.endDate') }}
      </dt>
      <dd class="text-[color:var(--color-text)]">
        {{ loan.endDate === undefined ? t('loans.notSet') : formatDate(loan.endDate, locale as 'en' | 'hu') }}
      </dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.rateType') }}
      </dt>
      <dd class="text-[color:var(--color-text)]">{{ rateTypeLabel }}</dd>
    </div>
    <div>
      <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{ t('loans.lender') }}
      </dt>
      <dd class="text-[color:var(--color-text)]">{{ loan.lender ?? t('loans.notSet') }}</dd>
    </div>
  </dl>
</template>
